/**
 * Jednotná sada SVG ikon – převzato z objektu `I` v prototypu.
 * Nahrazuje emoji z původní aplikace: emoji se renderují různě podle OS,
 * nedají se obarvit podle stavu a `📦` bylo použité 2× pro dva různé údaje.
 */
import type { SVGProps } from 'react';

export type IconName =
  | 'truck' | 'calx' | 'cal' | 'box' | 'uturn' | 'stopsign' | 'searchoff' | 'find'
  | 'quest' | 'check' | 'pin' | 'nav' | 'scan' | 'siren' | 'warn' | 'split'
  | 'border' | 'therm' | 'snow' | 'exit' | 'chevron';

const PATHS: Record<IconName, JSX.Element> = {
  truck: (
    <>
      <path d="M2 7h11v9H2zM13 10h4.5l3 3.2V16H13z" />
      <circle cx="6.5" cy="18.5" r="2" />
      <circle cx="17.5" cy="18.5" r="2" />
      <path d="M8.5 18.5h7" />
    </>
  ),
  calx: (
    <>
      <path d="M3.8 5.6h16.4v14.6H3.8z" />
      <path d="M3.8 10h16.4M8 3.4v3.4M16 3.4v3.4" />
      <path d="M9.6 13.6l4.8 4.4M14.4 13.6l-4.8 4.4" />
    </>
  ),
  cal: (
    <>
      <path d="M3.8 5.6h16.4v14.6H3.8z" />
      <path d="M3.8 10h16.4M8 3.4v3.4M16 3.4v3.4" />
    </>
  ),
  box: (
    <>
      <path d="M21 8.2v7.6L12 20.5 3 15.8V8.2L12 3.5z" />
      <path d="M3 8.2l9 4.6 9-4.6M12 12.8v7.7" />
    </>
  ),
  uturn: (
    <>
      <path d="M7 20V10a4.5 4.5 0 0 1 9 0v9" />
      <path d="M12.5 15.5 16 19.5l3.5-4" />
    </>
  ),
  stopsign: (
    <>
      <path d="M8.4 2.6h7.2L21.4 8.4v7.2l-5.8 5.8H8.4L2.6 15.6V8.4z" />
      <path d="M12 7.6v6" />
      <path d="M12 16.8h-.01" />
    </>
  ),
  searchoff: (
    <>
      <circle cx="10.6" cy="10.6" r="6.4" />
      <path d="M15.3 15.3 20.6 20.6" />
      <path d="M8.4 8.4l4.4 4.4M12.8 8.4l-4.4 4.4" />
    </>
  ),
  find: (
    <>
      <circle cx="11" cy="11" r="6.6" />
      <path d="M15.7 15.7 20.8 20.8" />
    </>
  ),
  quest: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.4 9.3a2.7 2.7 0 1 1 3.6 2.5v1.6" />
      <path d="M13 16.8h-.01" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M7.8 12.4l2.9 2.9 5.5-6.1" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),
  nav: <path d="M3 11l18-8-8 18-2-8z" />,
  scan: (
    <>
      <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
      <path d="M4 12h16" />
    </>
  ),
  siren: (
    <>
      <path d="M7 17v-4.5a5 5 0 0 1 10 0V17" />
      <path d="M4.5 20h15" />
      <path d="M12 3.5V2M19 6l1-1M5 6 4 5" />
    </>
  ),
  warn: (
    <>
      <path d="M12 4.5 21 19.5H3z" />
      <path d="M12 10v4M12 16.6h-.01" />
    </>
  ),
  split: (
    <>
      <path d="M3.5 4.5h9v6h-9zM11.5 13.5h9v6h-9z" />
      <path d="M8 10.5v3h3.5" />
    </>
  ),
  border: (
    <>
      <path d="M3 12h10.5" />
      <path d="M10.8 8.4 14.4 12l-3.6 3.6" />
      <path d="M18.6 3.4v17.2" strokeDasharray="3.2 3.2" />
    </>
  ),
  therm: (
    <>
      <path d="M14 14.2V5.4a2.2 2.2 0 1 0-4.4 0v8.8a3.6 3.6 0 1 0 4.4 0z" />
      <path d="M11.8 8.6v5.9" />
    </>
  ),
  snow: (
    <>
      <path d="M12 2.8v18.4M4 7.4l16 9.2M20 7.4 4 16.6" />
      <path d="M9.4 4.6 12 6.9l2.6-2.3M9.4 19.4 12 17.1l2.6 2.3" />
    </>
  ),
  exit: (
    <>
      <path d="M9.5 20H5.4A1.4 1.4 0 0 1 4 18.6V5.4A1.4 1.4 0 0 1 5.4 4h4.1" />
      <path d="M15.5 8.2 19.5 12l-4 3.8M19.5 12H9.8" />
    </>
  ),
  chevron: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
};

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
}

export function Icon({ name, ...rest }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...rest}>
      {PATHS[name]}
    </svg>
  );
}
